import type {
  DatasetDetail,
  DatasetRowsPage,
  DatasetSummary,
  TemplateDetail,
  TemplateCodeResponse,
  TemplateInputDatasetOption,
  TemplatePreviewResponse,
  TemplateRunProgress,
  TemplateRunRequest,
  TemplateRunResponse,
  TemplateRunSummary,
} from '@/types/templates';
import { normalizeDatasetRowsPagePayload } from '@/lib/datasetRows';

async function handleJsonResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const message = await response.text();
    throw new Error(message || response.statusText);
  }
  return (await response.json()) as T;
}

function normalizeTemplateRunProgress(value: unknown): TemplateRunProgress | undefined {
  if (!value || typeof value !== 'object') {
    return undefined;
  }
  const raw = value as Record<string, unknown>;
  const maxPages = raw.maxPages ?? raw.max_pages;
  const pagesAttempted = raw.pagesAttempted ?? raw.pages_attempted;
  const pagesSucceeded = raw.pagesSucceeded ?? raw.pages_succeeded;
  const rowsExtracted = raw.rowsExtracted ?? raw.rows_extracted;
  if (
    typeof maxPages !== 'number' ||
    typeof pagesAttempted !== 'number' ||
    typeof pagesSucceeded !== 'number' ||
    typeof rowsExtracted !== 'number'
  ) {
    return undefined;
  }
  const rawDebug = raw.debug;
  let debug: TemplateRunProgress['debug'] = null;
  if (rawDebug && typeof rawDebug === 'object') {
    const debugRecord = rawDebug as Record<string, unknown>;
    const screenshotRefsRaw = Array.isArray(debugRecord.screenshotRefs ?? debugRecord.screenshot_refs)
      ? (debugRecord.screenshotRefs ?? debugRecord.screenshot_refs)
      : [];
    const screenshotRefs = screenshotRefsRaw
      .filter((entry): entry is Record<string, unknown> => Boolean(entry && typeof entry === 'object'))
      .map((entry) => {
        const objectKeyRaw = entry.objectKey ?? entry.object_key;
        const objectKey = typeof objectKeyRaw === 'string' ? objectKeyRaw.trim() : '';
        if (!objectKey) {
          return null;
        }
        const pageIndexRaw = entry.pageIndex ?? entry.page_index;
        const pageUrlRaw = entry.pageUrl ?? entry.page_url;
        const screenshotUrlRaw = entry.screenshotUrl ?? entry.screenshot_url;
        return {
          objectKey,
          pageIndex: typeof pageIndexRaw === 'number' ? Number(pageIndexRaw) : null,
          pageUrl: typeof pageUrlRaw === 'string' ? pageUrlRaw : null,
          screenshotUrl: typeof screenshotUrlRaw === 'string' ? screenshotUrlRaw : null,
        };
      })
      .filter((entry): entry is NonNullable<typeof entry> => Boolean(entry))
      .slice(0, 5);
    const contractErrorsRaw = Array.isArray(debugRecord.contractErrors ?? debugRecord.contract_errors)
      ? (debugRecord.contractErrors ?? debugRecord.contract_errors)
      : [];
    debug = {
      enabled: Boolean(debugRecord.enabled),
      requiredPerPage: Boolean(debugRecord.requiredPerPage ?? debugRecord.required_per_page),
      maxPagesCap:
        typeof (debugRecord.maxPagesCap ?? debugRecord.max_pages_cap) === 'number'
          ? Number(debugRecord.maxPagesCap ?? debugRecord.max_pages_cap)
          : undefined,
      refLimit:
        typeof (debugRecord.refLimit ?? debugRecord.ref_limit) === 'number'
          ? Number(debugRecord.refLimit ?? debugRecord.ref_limit)
          : undefined,
      capturedCountTotal:
        typeof (debugRecord.capturedCountTotal ?? debugRecord.captured_count_total) === 'number'
          ? Number(debugRecord.capturedCountTotal ?? debugRecord.captured_count_total)
          : undefined,
      missingCountTotal:
        typeof (debugRecord.missingCountTotal ?? debugRecord.missing_count_total) === 'number'
          ? Number(debugRecord.missingCountTotal ?? debugRecord.missing_count_total)
          : undefined,
      contractFailed: Boolean(debugRecord.contractFailed ?? debugRecord.contract_failed),
      contractErrors: contractErrorsRaw
        .map((entry) => (typeof entry === 'string' ? entry : ''))
        .filter((entry) => entry.length > 0)
        .slice(0, 5),
      screenshotRefs,
    };
  }
  return {
    maxPages,
    pagesAttempted,
    pagesSucceeded,
    rowsExtracted,
    currentUrl:
      typeof (raw.currentUrl ?? raw.current_url) === 'string'
        ? String(raw.currentUrl ?? raw.current_url)
        : null,
    phase: typeof raw.phase === 'string' ? raw.phase : null,
    inputRowsTotal:
      typeof (raw.inputRowsTotal ?? raw.input_rows_total) === 'number'
        ? Number(raw.inputRowsTotal ?? raw.input_rows_total)
        : null,
    inputRowsProcessed:
      typeof (raw.inputRowsProcessed ?? raw.input_rows_processed) === 'number'
        ? Number(raw.inputRowsProcessed ?? raw.input_rows_processed)
        : null,
    batchesTotal:
      typeof (raw.batchesTotal ?? raw.batches_total) === 'number'
        ? Number(raw.batchesTotal ?? raw.batches_total)
        : null,
    batchesCompleted:
      typeof (raw.batchesCompleted ?? raw.batches_completed) === 'number'
        ? Number(raw.batchesCompleted ?? raw.batches_completed)
        : null,
    debug,
  };
}

function normalizeRequiredRunNamingFields(
  raw: Record<string, unknown>,
  context: 'response' | 'summary',
): Pick<TemplateRunResponse, 'displayName' | 'nameSource' | 'inputSummary'> {
  const displayNameRaw = raw.displayName ?? raw.display_name;
  const inputSummaryRaw = raw.inputSummary ?? raw.input_summary;
  const nameSourceRaw = raw.nameSource ?? raw.name_source;

  if (typeof displayNameRaw !== 'string' || displayNameRaw.trim().length === 0) {
    throw new Error(`Invalid template run ${context}: missing display_name`);
  }
  if (typeof inputSummaryRaw !== 'string' || inputSummaryRaw.trim().length === 0) {
    throw new Error(`Invalid template run ${context}: missing input_summary`);
  }
  if (nameSourceRaw !== 'system' && nameSourceRaw !== 'user') {
    throw new Error(`Invalid template run ${context}: missing name_source`);
  }

  return {
    displayName: displayNameRaw,
    inputSummary: inputSummaryRaw,
    nameSource: nameSourceRaw,
  };
}

function normalizeTemplateRunResponse(value: unknown): TemplateRunResponse {
  if (!value || typeof value !== 'object') {
    throw new Error('Invalid template run response');
  }
  const raw = value as Record<string, unknown>;
  const runId = raw.runId ?? raw.run_id;
  const status = raw.status;
  if (typeof runId !== 'string' || typeof status !== 'string') {
    throw new Error('Invalid template run response');
  }
  const naming = normalizeRequiredRunNamingFields(raw, 'response');
  return {
    runId,
    status: status as TemplateRunResponse['status'],
    datasetId: typeof (raw.datasetId ?? raw.dataset_id) === 'string' ? String(raw.datasetId ?? raw.dataset_id) : undefined,
    displayName: naming.displayName,
    nameSource: naming.nameSource,
    inputSummary: naming.inputSummary,
    message: typeof raw.message === 'string' ? raw.message : undefined,
    cancelRequested:
      typeof (raw.cancelRequested ?? raw.cancel_requested) === 'boolean'
        ? Boolean(raw.cancelRequested ?? raw.cancel_requested)
        : undefined,
    progress: normalizeTemplateRunProgress(raw.progress),
    statusUrl: typeof (raw.statusUrl ?? raw.status_url) === 'string' ? String(raw.statusUrl ?? raw.status_url) : undefined,
    websocket:
      typeof (raw.websocket ?? raw.websocket_url) === 'string'
        ? String(raw.websocket ?? raw.websocket_url)
        : undefined,
    websocketToken:
      typeof (raw.websocketToken ?? raw.websocket_token) === 'string'
        ? String(raw.websocketToken ?? raw.websocket_token)
        : undefined,
    websocketTokenExpiresAt:
      typeof (raw.websocketTokenExpiresAt ?? raw.websocket_token_expires_at) === 'number'
        ? Number(raw.websocketTokenExpiresAt ?? raw.websocket_token_expires_at)
        : undefined,
  };
}

function normalizeTemplateRunSummary(value: unknown): TemplateRunSummary {
  if (!value || typeof value !== 'object') {
    throw new Error('Invalid template run summary');
  }
  const raw = value as Record<string, unknown>;
  const runId = raw.runId ?? raw.run_id;
  const status = raw.status;
  const startedAt = raw.startedAt ?? raw.started_at;
  if (typeof runId !== 'string' || typeof status !== 'string' || typeof startedAt !== 'string') {
    throw new Error('Invalid template run summary');
  }
  const naming = normalizeRequiredRunNamingFields(raw, 'summary');
  return {
    runId,
    status: status as TemplateRunSummary['status'],
    datasetId: typeof (raw.datasetId ?? raw.dataset_id) === 'string' ? String(raw.datasetId ?? raw.dataset_id) : undefined,
    displayName: naming.displayName,
    nameSource: naming.nameSource,
    inputSummary: naming.inputSummary,
    startedAt,
    finishedAt:
      typeof (raw.finishedAt ?? raw.finished_at) === 'string'
        ? String(raw.finishedAt ?? raw.finished_at)
        : null,
    message: typeof raw.message === 'string' ? raw.message : null,
    cancelRequested:
      typeof (raw.cancelRequested ?? raw.cancel_requested) === 'boolean'
        ? Boolean(raw.cancelRequested ?? raw.cancel_requested)
        : undefined,
    progress: normalizeTemplateRunProgress(raw.progress),
    statusUrl: typeof (raw.statusUrl ?? raw.status_url) === 'string' ? String(raw.statusUrl ?? raw.status_url) : undefined,
    websocket:
      typeof (raw.websocket ?? raw.websocket_url) === 'string'
        ? String(raw.websocket ?? raw.websocket_url)
        : undefined,
    websocketToken:
      typeof (raw.websocketToken ?? raw.websocket_token) === 'string'
        ? String(raw.websocketToken ?? raw.websocket_token)
        : undefined,
    websocketTokenExpiresAt:
      typeof (raw.websocketTokenExpiresAt ?? raw.websocket_token_expires_at) === 'number'
        ? Number(raw.websocketTokenExpiresAt ?? raw.websocket_token_expires_at)
        : undefined,
  };
}


export async function fetchTemplate(id: string): Promise<TemplateDetail> {
  const response = await fetch(`/api/templates/${id}`, { cache: 'no-store' });
  return handleJsonResponse<TemplateDetail>(response);
}

export async function fetchTemplatePreview(id: string): Promise<TemplatePreviewResponse> {
  const response = await fetch(`/api/templates/${id}/preview`, { cache: 'no-store' });
  return handleJsonResponse<TemplatePreviewResponse>(response);
}

export async function fetchTemplateCode(id: string): Promise<TemplateCodeResponse> {
  const response = await fetch(`/api/templates/${id}/code`, { cache: 'no-store' });
  return handleJsonResponse<TemplateCodeResponse>(response);
}

export async function runTemplate(
  id: string,
  payload: TemplateRunRequest,
): Promise<TemplateRunResponse> {
  const body =
    payload.inputMode === 'dataset'
      ? {
          input_mode: 'dataset',
          parameters: payload.parameters ?? {},
          ...(payload.datasetName ? { dataset_name: payload.datasetName } : {}),
          dataset_input: {
            dataset_id: payload.datasetInput.datasetId,
            max_input_rows: payload.datasetInput.maxInputRows,
            parameter_mapping: payload.datasetInput.parameterMapping,
          },
        }
      : {
          parameters: payload.parameters,
          ...(payload.datasetName ? { dataset_name: payload.datasetName } : {}),
          ...(payload.maxPages ? { max_pages: payload.maxPages } : {}),
        };
  const response = await fetch(`/api/templates/${id}/runs`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  });
  const run = await handleJsonResponse<Record<string, unknown>>(response);
  return normalizeTemplateRunResponse(run);
}

export async function fetchTemplateInputDatasets(id: string): Promise<TemplateInputDatasetOption[]> {
  const response = await fetch(`/api/templates/${id}/input-datasets`, { cache: 'no-store' });
  const items = await handleJsonResponse<unknown[]>(response);
  return items
    .filter((item): item is Record<string, unknown> => Boolean(item && typeof item === 'object'))
    .map((item) => ({
      datasetId: String(item.datasetId ?? item.dataset_id ?? ''),
      name: String(item.name ?? ''),
      updatedAt: String(item.updatedAt ?? item.updated_at ?? ''),
      rowCount: typeof (item.rowCount ?? item.row_count) === 'number' ? Number(item.rowCount ?? item.row_count) : 0,
      sourceTemplateId: String(item.sourceTemplateId ?? item.source_template_id ?? ''),
      schemaColumns: Array.isArray(item.schemaColumns ?? item.schema_columns)
        ? (item.schemaColumns ?? item.schema_columns).filter(
            (value): value is string => typeof value === 'string' && value.trim().length > 0,
          )
        : [],
      defaultParameterMapping:
        item.defaultParameterMapping && typeof item.defaultParameterMapping === 'object'
          ? Object.fromEntries(
              Object.entries(item.defaultParameterMapping as Record<string, unknown>).filter(
                ([key, value]) => typeof key === 'string' && typeof value === 'string',
              ),
            )
          : {},
    }))
    .filter((item) => item.datasetId.length > 0);
}

export async function fetchTemplateRuns(
  id: string,
  limit = 5,
): Promise<TemplateRunSummary[]> {
  const query = Number.isFinite(limit) ? `?limit=${limit}` : '';
  const response = await fetch(`/api/templates/${id}/runs${query}`, { cache: 'no-store' });
  const runs = await handleJsonResponse<unknown[]>(response);
  return runs.map((run) => normalizeTemplateRunSummary(run));
}

export async function cancelTemplateRun(id: string, runId: string): Promise<TemplateRunResponse> {
  const response = await fetch(`/api/templates/${id}/runs/${runId}/cancel`, {
    method: 'POST',
    headers: { Accept: 'application/json' },
  });
  const run = await handleJsonResponse<Record<string, unknown>>(response);
  return normalizeTemplateRunResponse(run);
}

export async function renameTemplateRun(id: string, runId: string, displayName: string): Promise<TemplateRunSummary> {
  const response = await fetch(`/api/templates/${id}/runs/${runId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ display_name: displayName }),
  });
  const run = await handleJsonResponse<Record<string, unknown>>(response);
  return normalizeTemplateRunSummary(run);
}

export async function fetchDatasets(): Promise<DatasetSummary[]> {
  const response = await fetch('/api/datasets', { cache: 'no-store' });
  return handleJsonResponse<DatasetSummary[]>(response);
}

export async function fetchDataset(id: string): Promise<DatasetDetail> {
  const response = await fetch(`/api/datasets/${id}`, { cache: 'no-store' });
  return handleJsonResponse<DatasetDetail>(response);
}

export async function fetchDatasetRowsPage(
  id: string,
  options: { limit?: number; cursor?: string | null } = {},
): Promise<DatasetRowsPage> {
  const params = new URLSearchParams();
  if (options.limit && Number.isFinite(options.limit)) {
    params.set('limit', String(options.limit));
  }
  if (options.cursor) {
    params.set('cursor', options.cursor);
  }
  const query = params.toString();
  const response = await fetch(`/api/datasets/${id}/rows${query ? `?${query}` : ''}`, {
    cache: 'no-store',
  });
  const payload = await handleJsonResponse<Record<string, unknown>>(response);
  return normalizeDatasetRowsPagePayload(payload);
}

export async function exportDatasetCsv(id: string): Promise<{ url: string }> {
  const response = await fetch(`/api/datasets/${id}/exports`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
  });
  return handleJsonResponse<{ url: string }>(response);
}
